import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axios from 'axios';
import Timeline from './Timeline';
import 'primereact/resources/themes/saga-blue/theme.css'; // Import the theme
import 'primereact/resources/primereact.min.css'; // Core CSS
import 'primeicons/primeicons.css'; // Icons
import 'primeflex/primeflex.css'; // PrimeFlex CSS
import './GlobalStyles.css';
import { hasRoleAccess } from './utils/roles';

const EditShippingItem = ({ userRole }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [locationEntries, setLocationEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(100); // Number of items per page
  const [totalEntries, setTotalEntries] = useState(0); // Total items count
  const [sortField, setSortField] = useState('DatePlaced');
  const [sortOrder, setSortOrder] = useState(-1); // -1 for DESC, 1 for ASC
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [firstLocationPhoto, setFirstLocationPhoto] = useState(null);
  let processedReceiver = "";

  useEffect(() => {
    // Function to fetch the shipping item details
    const fetchItem = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/shipping-items/${id}`);
        setItem(response.data);
      } catch (error) {
        console.error('Error fetching item:', error);
      } finally {
        setLoading(false);
      }
    };

    // Define constants for retrying the location entries fetch
    const MAX_RETRIES = 5;
    const DELAY_MS = 3000; // 3 seconds

    // Function to fetch location entries with polling/retry
    const fetchLocationEntries = async (retryCount = 0) => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/shipping-item/${id}/location-entries`, {
          params: {
            page: page + 1,
            limit,
            sortField,
            sortOrder: sortOrder === 1 ? 'ASC' : 'DESC',
          },
        });
        const fetchedEntries = response.data || [];
        setLocationEntries(fetchedEntries);
        setTotalEntries(fetchedEntries.length);

        // If at least one location entry exists, attempt to load its photo document.
        if (fetchedEntries.length > 0) {
          const firstEntry = fetchedEntries[0];
          try {
            const docsResponse = await axios.get(`${apiBaseUrl}/api/location-entry/${firstEntry.EntryID}/documents`);
            if (docsResponse.data && docsResponse.data.length > 0) {
              // Look for a document that appears to be a photo (checks for "Photo" in EntryNotes)
              const firstDoc = docsResponse.data.find(doc => doc.EntryNotes && doc.EntryNotes.includes('Photo'));
              if (firstDoc) {
                const blobResponse = await axios.get(`${apiBaseUrl}/api/download-item/${firstEntry.EntryID}`, {
                  params: { fileName: firstDoc.FileName },
                  responseType: 'blob',
                });
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64String = reader.result.split(',')[1]; // Remove the data URL prefix
                  setFirstLocationPhoto(base64String);
                };
                reader.readAsDataURL(blobResponse.data);
              } else {
                setFirstLocationPhoto(null);
              }
            } else {
              setFirstLocationPhoto(null);
            }
          } catch (docsError) {
            // If the documents endpoint returns a 404, assume no documents exist.
            if (docsError.response && docsError.response.status === 404) {
              setFirstLocationPhoto(null);
            } else {
              console.error('Error fetching documents:', docsError);
            }
          }
        }
      } catch (error) {
        // If a 404 occurs on location entries and we haven't exceeded MAX_RETRIES, wait and retry.
        if (error.response && error.response.status === 404 && retryCount < MAX_RETRIES) {
          console.warn(
            `Location entries not found (retry ${retryCount + 1}/${MAX_RETRIES}). Retrying in ${DELAY_MS / 1000} seconds...`
          );
          setTimeout(() => {
            fetchLocationEntries(retryCount + 1);
          }, DELAY_MS);
        } else {
          console.error('Error fetching location entries:', error);
          alert('Failed to fetch location entries: ' + error.message);
        }
      }
    };

    fetchItem();
    fetchLocationEntries();
  }, [id, page, limit, sortField, sortOrder, apiBaseUrl]);

  const handleBack = () => {
    navigate('/shipping-items');
  };

  const handleSave = async () => {
    try {
      await axios.put(`${apiBaseUrl}/api/shipping-items/${id}`, item);
      alert('Item updated successfully');
      navigate(`/shipping-item/${id}`);
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Error updating item: ' + error.message);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== item.ShippingID) {
      alert('Shipping ID does not match. Please enter the correct Shipping ID to confirm deletion.');
      return;
    }
    try {
      await axios.delete(`${apiBaseUrl}/api/shipping-items/${id}`);
      alert('Item deleted successfully');
      navigate('/shipping-items');
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item: ' + error.message);
    }
  };

  const onInputChange = (e) => {
    const { name, value } = e.target;
    setItem({ ...item, [name]: value });
  };

  const onPageChange = (event) => {
    setPage(event.page);
    setLimit(event.rows);
  };

  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div>
        <Link to={`/view-location-entry/${rowData.EntryID}`}>
          <button className="intable-view-button">View</button>
        </Link>
        {hasRoleAccess(userRole, 'location-update') && (
          <Link to={`/edit-location-entry/${rowData.EntryID}`}>
            <button className="intable-edit-button">Edit</button>
          </Link>
        )}
      </div>
    );
  };

  const fetchProjectDetails = async () => {
    if (!item.ProjectNumber) {
      alert('Please enter a project number before validating.');
      return;
    }

    // Process the project number: split at dot and remove the "PN" prefix if present (case-insensitive)
    const baseProjectNumber = item.ProjectNumber.split('.')[0];
    let lookupProjectNumber = baseProjectNumber;
    if (/^pn/i.test(baseProjectNumber)) {
      lookupProjectNumber = baseProjectNumber.slice(2);
    }
    console.log('Lookup Project Number:', lookupProjectNumber);

    try {
      const response = await axios.get(`${apiBaseUrl}/api/sesd365/data`, {
        params: { projectNumber: lookupProjectNumber },
      });

      if (response.data && response.data.length > 0) {
        setItem({
          ...item,
          ProjectManager: response.data[0].ProjectManager_NameAlias,
          Company: response.data[0].CustomerName,
        });
      } else {
        alert('No project details found.');
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      alert('Error fetching project details: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!item) {
    return <div>Item not found</div>;
  }

  // Format the received date to ensure it's correct
  const receivedDate = new Date(item.Rcvd_Date).toISOString().split('T')[0];

  processedReceiver = item.Receiver == null ? "" : item.Receiver;

  return (
    <div className="edit-item-container">
      <h1>Edit Shipping Item</h1>

      {firstLocationPhoto && (
        <div className="first-location-photo-container">
          <img
            src={`data:image/jpeg;base64,${firstLocationPhoto}`}
            alt="First Location Photo"
            style={{ maxWidth: '300px', marginBottom: '1rem' }}
          />
        </div>
      )}

      <div className="item-details universal-form">
        {/* 1. Received Date */}
        <div className="universal-form-group">
          <label>Received Date:</label>
          <input
            type="date"
            name="Rcvd_Date"
            value={receivedDate}
            onChange={onInputChange}
          />
        </div>
        {/* 2. Shipping ID */}
        <div className="universal-form-group">
          <label>Shipping ID:</label>
          <span>{item.ShippingID}</span>
        </div>
        {/* 3. Quantity in Lot */}
        <div className="universal-form-group">
          <label>Quantity in Lot:</label>
          <input
            type="number"
            name="QtyInLot"
            value={item.QtyInLot}
            onChange={onInputChange}
          />
        </div>
        {/* 4. Project Number */}
        <div className="universal-form-group">
          <label>Project Number:</label>
          <input
            type="text"
            name="ProjectNumber"
            value={item.ProjectNumber}
            onChange={onInputChange}
          />
          <button onClick={fetchProjectDetails} className="validate-button">
            Get Project Info
          </button>
        </div>
        {/* 5. Project Manager */}
        <div className="universal-form-group">
          <label>Project Manager:</label>
          <input
            type="text"
            name="ProjectManager"
            value={item.ProjectManager}
            readOnly
          />
        </div>
        {/* 6. Client */}
        <div className="universal-form-group">
          <label>Client:</label>
          <input
            type="text"
            name="Company"
            value={item.Company}
            readOnly
          />
        </div>
        {/* 7. Carrier */}
        <div className="universal-form-group">
          <label>Carrier:</label>
          <input
            type="text"
            name="Carrier"
            value={item.Carrier || ""}
            onChange={onInputChange}
          />
        </div>
        {/* 8. Shipper */}
        <div className="universal-form-group">
          <label>Shipper:</label>
          <input
            type="text"
            name="Shipper"
            value={item.Shipper || ""}
            onChange={onInputChange}
          />
        </div>
        {/* 9. Description */}
        <div className="universal-form-group">
          <label>Description:</label>
          <input
            type="text"
            name="Description"
            value={item.Description}
            onChange={onInputChange}
          />
        </div>
        {/* 10. Receiver */}
        <div className="universal-form-group">
          <label>Receiver:</label>
          <input
            type="text"
            name="Receiver"
            value={processedReceiver}
            readOnly
          />
        </div>
      </div>

      <Timeline entries={locationEntries.slice().reverse()} receivedDate={receivedDate} />
      <div className="location-entries-header">
        <h2>All Location Entries</h2>
        <Link to={`/create-location-entry/${id}`} className="link-button">
          <button className="create-location-button">Create Location Entry</button>
        </Link>
      </div>
      <div className="table-container">
        <DataTable
          value={locationEntries}
          paginator
          rows={limit}
          totalRecords={totalEntries}
          loading={loading}
          lazy
          first={page * limit}
          onPage={onPageChange}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
        >
          <Column field="EntryID" header="Entry ID" sortable />
          <Column field="Location" header="Location" sortable />
          <Column field="DatePlaced" header="Date Placed" sortable />
          <Column field="Notes" header="Notes" sortable />
          <Column field="Qty" header="Quantity" sortable />
          <Column body={actionBodyTemplate} header="Actions" />
        </DataTable>
      </div>
      <button onClick={handleSave} className="global-save-button">Save</button>
      <button onClick={handleBack} className="global-back-button">Back</button>
      {hasRoleAccess(userRole, 'shipping-delete') && (
        <>
          <button onClick={() => setShowDeleteDialog(true)} className="global-delete-button">
            Delete Shipping Item
          </button>
          {showDeleteDialog && (
            <div className="delete-dialog">
              <p>Type the Shipping ID to confirm deletion:</p>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
              />
              <button onClick={handleDelete} className="confirm-delete-button">
                Confirm Delete
              </button>
              <button onClick={() => setShowDeleteDialog(false)} className="cancel-delete-button">
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EditShippingItem;
